import * as React from "react"
import {CmsContent} from "../../Model/CmsContent/CmsContent";
import {optHtmlContent, renderDynamicContent} from "../../Utilities/CmsUtilities";
import {SimOnlyPlan, simOnlyPlanDataLimit, simOnlyPlanMonthlyCost} from "../../Model/Sales/SimOnlyPlan";
import {useNavigate, useParams, useSearchParams} from "react-router-dom";
import {ApplicationContext} from "../../ApplicationContext";
import {getActivationCodeQueryParam, getPromoQueryParam, SALES_PORTAL_ROUTES} from "../../Routes/SalesPortalRoutes";
import {
    fetchSimOnlyPlanByCode,
    fetchSimOnlyPlanByPlanCodeAndPromoCode,
    fetchSimOnlyPlans
} from "../../Api/Sales/SimOnlyPlanApi";
import {isRequestError, RequestError} from "../../Model/RequestError";
import HeaderFooterWrapper from "../../Components/Navigation/HeaderFooterWrapper";
import {cardPaymentMethodPayload, Dropin} from "braintree-web-drop-in";
import {createOrder} from "../../Api/Sales/OrderApi";
import dayjs from "dayjs";
import {applyInputError, toggleInputError} from "../../Utilities/CmsFormUtilities";
import {PlanPrice} from "../../Model/Sales/PlanPrice";
import {fetchPriceForPlan, fetchPriceForPlanWithPromo} from "../../Api/Sales/PricesApi";
import {truncateString} from "../../Utilities/StringUtilities";
import {RafData, sessionStorageRafKey} from "../RafPage/RafPage";
import {CmsFormSchema} from "../../Model/CmsContent/CmsFormSchema";
import {doesNotContainSpecialCharacters, isMaxLength, isNonEmptyString} from "../../Utilities/ValidationUtilities";
import {
    PATTERN_ALPHA_NUMERIC_W_SPACE_COMMA_PERIOD,
    PATTERN_POSTCODE,
    PATTERN_SIMPLE_EMAIL
} from "../../Constant/ValidationRegex";
import {CreateAddress} from "../../Model/Sales/CreateOrder";
import {isEmailAssociatedWithCustomer} from "../../Api/Sales/CustomerProfileApi";
import {OrderConfirmation} from "../../Model/Sales/OrderConfirmation";
import {saveLocalStorageItem} from "../../Utilities/LocalStorageUtilities";
import {readSessionItem} from "../../Utilities/SessionUtilities";
import {isSimDetails} from "../../Model/Sim/SimDetails";
import SimOnlyPlanOnePagePurchaseForm from "./Components/SimOnlyPlanOnePagePurchaseForm";
import {fetchPlanCardContent} from "../../Api/Sales/PlansApi";
import {ContentType} from "../../Model/Sales/ContentType";
import LoadingBackdrop from "../../Components/LoadingBackdrop/LoadingBackdrop";
import {fetchPromoPlanCardContent} from "../../Api/Sales/PromotionsApi";
import TagManager from "react-gtm-module";
import PlanPurchasePaymentSummary from "./Components/PlanPurchasePaymentSummary";
import {useMediaQuery} from "../../Utilities/WindowUtilities";
import SimCardInfo from "./Components/SimCardInfo";
import SimCardInfoSolo from "./Components/SimCardInfoSolo";
import {fetchCarouselContent} from "../../Api/Sales/CarouselApi";
import {CarouselContentType} from "../../Model/Sales/CarouselContentType";
import {CarouselContentItem} from "../../Model/Sales/CarouselContentItem";
import {verifyPortDetails} from "../../Api/Sales/SwitchingApi";

interface SimOnlyPlanPurchaseOnlyPageProps {
    readonly simOnlyPlans: SimOnlyPlan[];

    /**
     * Array of all content currently stored in the CMS that has been made available to the Sales Portal.
     */
    readonly cmsContent: CmsContent[]

}
/**
 * Function will render the Superdrug-specific customer signup page. This page will create an order
 * based on information provided by Hybris and the plan selected earlier in the signup journey.
 */
const SimOnlyPlanOnePagePurchasePage = (props: SimOnlyPlanPurchaseOnlyPageProps): JSX.Element | null => {

    const applicationContext = React.useContext(ApplicationContext)
    const navigate = useNavigate()
    const searchParamsTuple = useSearchParams()
    const {planCode} = useParams<keyof { readonly planCode: string }>()

    const [expandedCardPackageCode, setExpandedCardPackageCode] = React.useState<string | null>(null);
    const [planCardContentMap, setPlanCardContentMap] = React.useState<
        Record<string, { hexColor: string }>
    >({});

    const [componentData, setComponentData] = React.useState<{
        readonly selectedPlan: SimOnlyPlan
        readonly simOnlyPlans: SimOnlyPlan[]
        readonly selectedPlanPriceWithPromo: PlanPrice | null,
        readonly planHexColor: string
    }>()
    const [promoCode, setPromoCode] = React.useState<string | null>(
        () => getPromoQueryParam(searchParamsTuple)
    )
    const [activationCode, setActivationCode] = React.useState<string | null>(
        () => getActivationCodeQueryParam(searchParamsTuple)
    )

    const [loading, setLoading] = React.useState<boolean>(true)

    React.useEffect(() => {
        setActivationCode(getActivationCodeQueryParam(searchParamsTuple));
    }, [searchParamsTuple]);

    const [braintreeInstance, setBraintreeInstance] = React.useState<Dropin>()
    const [isSimInHandJourney, setIsSimInHandJourney] = React.useState<boolean>();
    const [hasLeadGenerator, setHasLeadGenerator] = React.useState<boolean>();
    const [paidInStorePlanPrice, setPaidInStorePlanPrice] = React.useState<number>();
    const [selectedPlanIndex, setSelectedPlanIndex] = React.useState<number>()

    const formId = "form-one-page-purchase-sign-up"

    const isDesktop = useMediaQuery("(min-width: 1024px)");

    const setupComponent = async () => {
        const selectedPlanCode = planCode ?? applicationContext.appConfig.signupConfiguration.defaultPlan!!
        const optSimDetails = readSessionItem("sim-details", isSimDetails)
        const simOnlyPlans = await fetchSimOnlyPlans(applicationContext.reseller, applicationContext.salesPortalApiDomain, true)
        if (isRequestError(simOnlyPlans)) {
            navigate(SALES_PORTAL_ROUTES.InternalError(applicationContext.urlContext))
            return
        }
        let activationCodeValid = false

        if (activationCode != null) {
            if (optSimDetails == null) {
                navigate(SALES_PORTAL_ROUTES.InternalError(applicationContext.urlContext))
                return
            }
            const last12DigitsOfIccid = optSimDetails.iccid.slice(-12)
            if (activationCode != last12DigitsOfIccid) {
                navigate(SALES_PORTAL_ROUTES.InternalError(applicationContext.urlContext))
                return
            }
            activationCodeValid = true
        }

        const isASimInHandJourney = activationCode != null && activationCodeValid
        if (isASimInHandJourney && optSimDetails!!.leadGenerator == null) {
            const paidInStorePlan = await fetchSimOnlyPlanByCode(
                applicationContext.reseller, applicationContext.salesPortalApiDomain, applicationContext.appConfig.signupConfiguration.defaultPlan!!)
            if (isRequestError(paidInStorePlan)) {
                navigate(SALES_PORTAL_ROUTES.InternalError(applicationContext.urlContext))
                return
            }
            setHasLeadGenerator(false)
            setPaidInStorePlanPrice(paidInStorePlan.monthlyPrice)
        } else {
            setHasLeadGenerator(true)
            setPaidInStorePlanPrice(0)
        }

        setIsSimInHandJourney(isASimInHandJourney)

        let planHexColor: string | undefined = ""

        let plans: SimOnlyPlan[] = []

        const carouselResult: CarouselContentItem[] | RequestError = await fetchCarouselContent(
            applicationContext.salesPortalApiDomain,
            applicationContext.reseller,
            CarouselContentType.STANDARD
        )
        if (isRequestError(carouselResult)) {
            navigate(SALES_PORTAL_ROUTES.InternalError(applicationContext.urlContext))
            return
        } else {
            carouselResult.sort((a, b) => a.orderIndex - b.orderIndex);

            plans = props.simOnlyPlans.filter(plan => {
                return carouselResult.some(carouselItem =>
                    carouselItem.planCode === plan.packageCode && carouselItem.promoCode === plan.promoCode)
            })
        }

        const maybePlan = !isASimInHandJourney ?
            // If it's web sale then grab the plan and promo code combo
            await fetchSimOnlyPlanByPlanCodeAndPromoCode(
                applicationContext.reseller, applicationContext.salesPortalApiDomain, selectedPlanCode, promoCode) :
            // If it's SIH then find the plan from the carousel options
            plans.find(
                plan =>
                    plan.packageCode === selectedPlanCode &&
                    plan.promoCode === promoCode
            ) ?? // If we can't find the plan with the promo back up any plan with that code
            plans.find(
                plan => plan.packageCode === selectedPlanCode
            );
        const queryParams = new URLSearchParams(window.location.search);

        // We don't use the query param for promos in SIH journey so get rid of it
        if (isASimInHandJourney) {
            setPromoCode("")
            queryParams.delete("promo");
            const newUrl = `${window.location.origin}${window.location.pathname}?${queryParams.toString()}`;
            window.history.replaceState(null, '', newUrl);
        }

        if (!maybePlan || isRequestError(maybePlan)) {

            navigate(SALES_PORTAL_ROUTES.InternalError(applicationContext.urlContext))
            return
        }

        if (selectedPlanIndex) {
            plans = plans.map((plan, index) =>
                index == selectedPlanIndex
                    ? maybePlan
                    : plan
            )
        } else if (!plans.includes(maybePlan)) {
            plans.push(maybePlan)
        }


        if (!isASimInHandJourney) {
            planHexColor = await getPlanHexColour(maybePlan)
        }

        let planPriceWithPromo: PlanPrice | null
        planPriceWithPromo = null
        if (promoCode) {

            const maybePlanPrice = await fetchPriceForPlanWithPromo(
                applicationContext.reseller, applicationContext.salesPortalApiDomain, selectedPlanCode, promoCode)
            if (isRequestError(maybePlanPrice)) {
                navigate(SALES_PORTAL_ROUTES.InternalError(applicationContext.urlContext))
                return
            }
            planPriceWithPromo = maybePlanPrice
        }

        setComponentData({
            selectedPlan: maybePlan,
            simOnlyPlans: plans,
            selectedPlanPriceWithPromo: planPriceWithPromo,
            planHexColor: planHexColor != undefined ? planHexColor : ""
        })

        setSelectedPlanIndex(plans.findIndex(plan => maybePlan.packageCode === plan.packageCode &&
            maybePlan.promoCode === plan.promoCode))

        setLoading(false)

    }

    const getPlanHexColour = async (plan: SimOnlyPlan) => {
        const result: CmsContent[] | RequestError = promoCode == null || promoCode == "" ? await fetchPlanCardContent(
            applicationContext.salesPortalApiDomain,
            applicationContext.reseller,
            ContentType.PLAN_EXPANDED,
            plan.packageCode
        ) : await fetchPromoPlanCardContent(
            applicationContext.salesPortalApiDomain,
            ContentType.PROMO_EXPANDED,
            plan.packageCode,
            promoCode
        )
        if (isRequestError(result)) {
            navigate(SALES_PORTAL_ROUTES.InternalError(applicationContext.urlContext))
            return
        } else {
            return result[0].hexColor
        }
    }

    React.useEffect(() => {
        setupComponent()
    }, []);

    const updateComponent = async () => {
        if (!componentData || !planCode) return
        let newPlan: SimOnlyPlan | null = null

        let planPriceWithPromo: PlanPrice | null = null

        const maybeNewPlan = await fetchSimOnlyPlanByPlanCodeAndPromoCode(
            applicationContext.reseller, applicationContext.salesPortalApiDomain,
            planCode, promoCode)

        if (isRequestError(maybeNewPlan)) {
            navigate(SALES_PORTAL_ROUTES.InternalError(applicationContext.urlContext))
            return
        }

        newPlan = maybeNewPlan

        const maybePlanPrice = promoCode ? await fetchPriceForPlanWithPromo(
                applicationContext.reseller, applicationContext.salesPortalApiDomain,
                planCode, promoCode) :
            await fetchPriceForPlan(applicationContext.reseller, applicationContext.salesPortalApiDomain,
                planCode)

        if (isRequestError(maybePlanPrice)) {
            navigate(SALES_PORTAL_ROUTES.InternalError(applicationContext.urlContext))
            return
        }

        planPriceWithPromo = maybePlanPrice

        const newPlans = componentData.simOnlyPlans.map((plan, index) =>
            index == selectedPlanIndex
                ? newPlan!
                : plan
        )

        let planHexColor: string | undefined
        if (!isSimInHandJourney) {
            planHexColor = await getPlanHexColour(newPlan)
        }
        if (newPlans) {
            setComponentData({
                selectedPlan: newPlan,
                simOnlyPlans: newPlans,
                selectedPlanPriceWithPromo: planPriceWithPromo,
                planHexColor: planHexColor != undefined ? planHexColor : ""
            })
        }

    }

    React.useEffect(() => {
        updateComponent()

    }, [promoCode, planCode]);

    React.useEffect(() => {
        const fetchCardContent = async () => {
            if (!componentData) return;

            const promises = componentData.simOnlyPlans.map(async (simOnlyPlan) => {
                const result =
                    simOnlyPlan.promoCode == null || simOnlyPlan.promoCode === ""
                        ? await fetchPlanCardContent(
                            applicationContext.salesPortalApiDomain,
                            applicationContext.reseller,
                            ContentType.PLAN_EXPANDED,
                            simOnlyPlan.packageCode
                        )
                        : await fetchPromoPlanCardContent(
                            applicationContext.salesPortalApiDomain,
                            ContentType.PROMO_EXPANDED,
                            simOnlyPlan.packageCode,
                            simOnlyPlan.promoCode
                        );

                if (!isRequestError(result)) {
                    return {
                        packageCode: simOnlyPlan.packageCode,
                        hexColor: result[0].hexColor
                    };
                }

                return null;
            });

            const results = await Promise.all(promises);

            const contentMap: Record<string, { hexColor: string }> = {};

            results.forEach(item => {
                if (item) {
                    contentMap[item.packageCode] = {
                        hexColor: item.hexColor
                    };
                }
            });

            setPlanCardContentMap(contentMap);
        };

        fetchCardContent();
    }, [componentData?.simOnlyPlans]);

    const optPurchasePage = optHtmlContent(props.cmsContent, "sp-page-purchase-and-sign-up")

    const setSelectedPlanPriceWithPromo = (planPrice: PlanPrice | null) => {
        setComponentData(prevState => ({
            ...prevState!,
            selectedPlanPriceWithPromo: planPrice
        }))
    }

    const setSelectedPlan = (simOnlyPlan: SimOnlyPlan) => {
        setComponentData(prevState => ({
            ...prevState!,
            selectedPlan: simOnlyPlan
        }))
    }

    const optOtherPlanCard = optHtmlContent(props.cmsContent, "sp-card-sim-only-plan-other")
    const optSelectedOtherPlanCard = optHtmlContent(props.cmsContent, "sp-card-sim-only-plan-other-selected")

    // Plan with all you need label
    const optOtherPlanCardAllYouNeed = optHtmlContent(props.cmsContent, "sp-card-sim-only-plan-other-all-you-need")
    const optSelectedOtherPlanCardAllYouNeed = optHtmlContent(props.cmsContent, "sp-card-sim-only-plan-other-selected-all-you-need")

    if (componentData && optOtherPlanCard && optSelectedOtherPlanCard &&
        optOtherPlanCardAllYouNeed && optSelectedOtherPlanCardAllYouNeed && optPurchasePage) {

        let planIndex = 0

        const simOnlyPlanCards = componentData.simOnlyPlans.map(simOnlyPlan => {
            const selected = planIndex == selectedPlanIndex

            const cardContent = planCardContentMap[simOnlyPlan.packageCode];

            if (!cardContent) return null;

            const simCardInfo = <SimCardInfo
                onCardClick={() => setExpandedCardPackageCode(null)}
                onButtonClick={setExpandedCardPackageCode}
                setSelectedPlan={setSelectedPlan}
                setSelectedPlanIndex={setSelectedPlanIndex}
                cmsContent={props.cmsContent}
                index={planIndex}
                selected={selected}
                expanded={expandedCardPackageCode == simOnlyPlan.packageCode}
                simOnlyPlan={simOnlyPlan}
                activationCode={activationCode}
                planHexColor={cardContent.hexColor}
                setPromoCode={setPromoCode}
            />

            planIndex++
            return simCardInfo
        })

        const chosenPlanInfo = isSimInHandJourney ? componentData.simOnlyPlans.find(simOnlyPlan =>
            simOnlyPlan.packageCode === componentData.selectedPlan.packageCode &&
            simOnlyPlan.promoCode === componentData.selectedPlan.promoCode
        ) : componentData.simOnlyPlans.find(simOnlyPlan =>
            simOnlyPlan.packageCode === componentData.selectedPlan.packageCode &&
            simOnlyPlan.promoCode === componentData.selectedPlan.promoCode )

        const deliveryPrice = 0

        const maybeRafSessionData = sessionStorage.getItem(sessionStorageRafKey)
        const maybeRafData: RafData | null = maybeRafSessionData ? JSON.parse(maybeRafSessionData) : null;

        const submitForm = async (formData: FormData): Promise<void> => {

            const formCmsContent = props.cmsContent.find((content) => content.reference === formId)
            if (!formCmsContent) {
                return
            }
            const formSchema = formCmsContent.jsonContent as CmsFormSchema
            const errorMap = formSchema.errorMap
            const email = formData.get("email-address")
            const emailNotBlank = isNonEmptyString(email)
            let emailIsEmail

            let emailIsAssociatedWithCustomer = true;

            if (emailNotBlank) {
                emailIsEmail = PATTERN_SIMPLE_EMAIL.test(email)
                const result = await isEmailAssociatedWithCustomer(
                    applicationContext.salesPortalApiDomain,
                    email
                );
                if (isRequestError(result)) {
                    console.log("Error checking if email is in use:", result);
                } else {
                    emailIsAssociatedWithCustomer = result.exists;
                }
            }

            const emailIsValid = emailNotBlank && emailIsEmail && !emailIsAssociatedWithCustomer
            applyInputError(errorMap, formId, "email-address", !emailIsAssociatedWithCustomer ? undefined : "An account with the email provided may already exist")
            applyInputError(errorMap, formId, "email-address", emailIsEmail ? undefined : "A valid email address was expected but was not provided")
            applyInputError(errorMap, formId, "email-address", emailNotBlank ? undefined : "An email address was expected but was not provided")
            if (emailIsValid) {
                toggleInputError(errorMap, formId, "email-address", undefined)
            }

            const emailConfirmation = formData.get("email-address-confirmation")
            const emailConfirmationNotBlank = isNonEmptyString(email)
            const emailConfirmationMatches = email === emailConfirmation
            const emailConfirmationValid = emailConfirmationMatches && emailConfirmationNotBlank
            applyInputError(errorMap, formId, "email-address-confirmation", emailConfirmationNotBlank ? undefined : "An email address was expected but was not provided")
            applyInputError(errorMap, formId, "email-address-confirmation", emailConfirmationMatches ? undefined : "The emails do not match")
            if (emailConfirmationValid) {
                toggleInputError(errorMap, formId, "email-address-confirmation", undefined)
            }

            const dateOfBirth = formData.get("dateOfBirth")
            const dateOfBirthValid = isNonEmptyString(dateOfBirth) && /^\d{4}-\d{2}-\d{2}$/.test(dateOfBirth)
            toggleInputError(errorMap, formId, "dateOfBirth", dateOfBirthValid ? undefined : "A valid date of birth was expected but was not provided")

            let ageIsValid = true
            if (dateOfBirthValid && applicationContext.appConfig.signupConfiguration.minimumSignupAge) {
                const age = dayjs().diff(dayjs(dateOfBirth), 'years')
                ageIsValid = age >= applicationContext.appConfig.signupConfiguration.minimumSignupAge && age <= 120
                toggleInputError(errorMap, formId, "dateOfBirth", ageIsValid ? undefined : `You must be at least ${ applicationContext.appConfig.signupConfiguration.minimumSignupAge} years old to sign up`)
            }

            const forename = formData.get("forename")
            const forenameNotBlank = isNonEmptyString(forename)
            const forenameLengthLessThan30 = isMaxLength(forename, 30)
            const forenameNotContainsSpecialCharacters = doesNotContainSpecialCharacters(forename)
            const isForenameValid = forenameNotBlank && forenameLengthLessThan30 && forenameNotContainsSpecialCharacters
            applyInputError(errorMap, formId, "forename", forenameNotBlank ? undefined : "A forename was expected but was not provided")
            applyInputError(errorMap, formId, "forename", forenameLengthLessThan30 ? undefined : "The given forename exceeded the maximum length")
            applyInputError(errorMap, formId, "forename", forenameNotContainsSpecialCharacters ? undefined : "The given forename included special characters")
            if (isForenameValid) {
                toggleInputError(errorMap, formId, "forename", undefined)
            }

            const surname = formData.get("surname")
            const surnameNotBlank = isNonEmptyString(surname)
            const surnameLengthLessThan30 = isMaxLength(surname, 30)
            const surnameNotContainsSpecialCharacters = doesNotContainSpecialCharacters(surname)
            const isSurnameValid = surnameNotBlank && surnameLengthLessThan30 && surnameNotContainsSpecialCharacters
            applyInputError(errorMap, formId, "surname", surnameNotBlank ? undefined : "A surname was expected but was not provided")
            applyInputError(errorMap, formId, "surname", surnameLengthLessThan30 ? undefined : "The given surname exceeded the maximum length")
            applyInputError(errorMap, formId, "surname", surnameNotContainsSpecialCharacters ? undefined : "The given surname included special characters")
            if (isSurnameValid) {
                toggleInputError(errorMap, formId, "surname", undefined)
            }

            const previewCheckbox = document.getElementById(
                "previewCheckbox"
            ) as HTMLInputElement | null

            const previewAgreedValid: boolean =
                previewCheckbox !== null && previewCheckbox.checked
            toggleInputError(errorMap, formId, "previewCheckbox", previewAgreedValid ? undefined : "You must agree to our terms and conditions")

            const validateAddress = (
                prefix: "billing" | "delivery",
                line1: FormDataEntryValue | null,
                line2: FormDataEntryValue | null,
                city: FormDataEntryValue | null,
                postcode: FormDataEntryValue | null) => {

                const line1NotBlank = isNonEmptyString(line1)
                const line1LengthLessThan30 = !isNonEmptyString(line1) || line1.length <= 30
                const line1AlphaNumericCharacters = !isNonEmptyString(line1) || PATTERN_ALPHA_NUMERIC_W_SPACE_COMMA_PERIOD.test(line1)
                const line1Valid = line1NotBlank && line1LengthLessThan30 && line1AlphaNumericCharacters
                applyInputError(errorMap, formId, `${prefix}Line1`, line1NotBlank ? undefined : "A line1 was expected but was not provided")
                applyInputError(errorMap, formId, `${prefix}Line1`, line1LengthLessThan30 ? undefined : "The given line1 exceeded the maximum length")
                applyInputError(errorMap, formId, `${prefix}Line1`, line1AlphaNumericCharacters ? undefined : "The given line1 included special characters")
                if (line1Valid) {
                    toggleInputError(errorMap, formId, `${prefix}Line1`, undefined)
                }

                const line2LengthLessThan30 = !isNonEmptyString(line2) || line2.length <= 30
                const line2AlphaNumeric = !isNonEmptyString(line2) || PATTERN_ALPHA_NUMERIC_W_SPACE_COMMA_PERIOD.test(line2)
                const line2Valid = line2LengthLessThan30 && line2AlphaNumeric

                applyInputError(errorMap, formId, `${prefix}Line2`, line2LengthLessThan30 ? undefined : "The given line2 exceeded the maximum length")
                applyInputError(errorMap, formId, `${prefix}Line2`, line2AlphaNumeric ? undefined : "The given line2 included special characters")
                if (line2Valid) {
                    toggleInputError(errorMap, formId, `${prefix}Line2`, undefined)
                }


                const townNotBlank = isNonEmptyString(city)
                const townLengthLessThan30 = !isNonEmptyString(city) || city.length <= 30
                const townValid = townNotBlank && townLengthLessThan30
                applyInputError(errorMap, formId, `${prefix}City`, townNotBlank ? undefined : "A town was expected but was not provided")
                applyInputError(errorMap, formId, `${prefix}City`, townLengthLessThan30 ? undefined : "The given town included special characters")
                if (townValid) {
                    toggleInputError(errorMap, formId, `${prefix}City`, undefined)
                }


                const postCodeNotBlank = isNonEmptyString(postcode)
                const postCodePatternValid = !isNonEmptyString(postcode) || PATTERN_POSTCODE.test(postcode)
                const postCodeValid = postCodeNotBlank && postCodePatternValid
                applyInputError(errorMap, formId, `${prefix}Postcode`, postCodeNotBlank ? undefined : "A postCode was expected but was not provided")
                applyInputError(errorMap, formId, `${prefix}Postcode`, postCodePatternValid ? undefined : "The given postCode was not valid")
                if (postCodeValid) {
                    toggleInputError(errorMap, formId, `${prefix}Postcode`, undefined)
                }

                return postCodeValid && townValid && line1Valid && line2Valid
            }

            const billingLine1 = formData.get("billingLine1")
            const billingLine2 = formData.get("billingLine2")
            const billingCity = formData.get("billingCity")
            const billingPostcode = formData.get("billingPostcode")

            const isBillingAddressValid = validateAddress("billing", billingLine1, billingLine2, billingCity, billingPostcode)

            const billingLine2NotBlank = isNonEmptyString(billingLine2)

            const useBillingAsDelivery = isSimInHandJourney || formData.get("useBillingAddressAsDelivery") === "on"

            const deliveryLine1 = formData.get("deliveryLine1")
            const deliveryLine2 = formData.get("deliveryLine2")
            const deliveryCity = formData.get("deliveryCity")
            const deliveryPostcode = formData.get("deliveryPostcode")

            const deliveryLine2NotBlank = isNonEmptyString(deliveryLine2)

            const deliveryAddressValid = useBillingAsDelivery ||
                validateAddress("delivery", deliveryLine1, deliveryLine2, deliveryCity, deliveryPostcode)

            const portInMsisdn = document.getElementById(
                "portInNumber"
            ) as HTMLInputElement | null

            const isPortInMsisdnEmpty: boolean =
                portInMsisdn !== null && (portInMsisdn.value.length === 0)

            const pacCode = document.getElementById(
                "pacCode"
            ) as HTMLInputElement | null

            const isPacCodeEmpty: boolean = pacCode !== null && (pacCode.value.length === 0)

            const isPortInMsisdnValid: boolean =
                portInMsisdn == null || ((isPortInMsisdnEmpty && isPacCodeEmpty) ||
                    /.*(s*(447[0-9]{9})s*)|(s*(07[0-9]{9})s*)/.test(portInMsisdn.value))

            applyInputError(errorMap, formId, "portInNumber", isPortInMsisdnValid ? undefined : "A valid UK mobile number must be provided or none at all")
            if (isPortInMsisdnValid) {
                toggleInputError(errorMap, formId, `portInNumber`, undefined)
            }

            const isPacCodeValid: boolean =
                pacCode == null || ((isPacCodeEmpty && isPortInMsisdnEmpty) ||
                    /(^[A-Z]{3}[0-9]{6}$)|(^[0-9]{6}[A-Z]{3}$)/.test(pacCode.value))

            applyInputError(errorMap, formId, "pacCode", isPacCodeValid ? undefined : "A valid switching code must be provided or none at all")
            if (isPacCodeValid) {
                toggleInputError(errorMap, formId, `pacCode`, undefined)
            }

            let isPortInDetailsValid = true
            if (pacCode?.value && portInMsisdn?.value) {
                const maybePortVerification = await verifyPortDetails(applicationContext.salesPortalApiDomain, {
                    msisdn: portInMsisdn.value,
                    pac: pacCode.value
                })
                if (isRequestError(maybePortVerification)) {
                    isPortInDetailsValid = false
                    applyInputError(errorMap, formId, "portInDetails", isPortInDetailsValid ? undefined : "Unable to check validity of port details")
                } else if (!maybePortVerification.portDetailsValid){
                    isPortInDetailsValid = false
                    applyInputError(errorMap, formId, "portInDetails", isPortInDetailsValid ? undefined : "Invalid switching code and mobile number combination")
                }
            }
            if (isPortInDetailsValid) {
                toggleInputError(errorMap, formId, `portInDetails`, undefined)
            }

            if (isBillingAddressValid && deliveryAddressValid && isSurnameValid && isForenameValid && dateOfBirthValid && emailConfirmationValid
                && previewAgreedValid && emailIsValid && ageIsValid && chosenPlanInfo && isPortInMsisdnValid && isPacCodeValid && isPortInDetailsValid) {

                const billingSplitPostcodeArray = (billingPostcode as string)
                    .replace(/\s/g, "")
                    .match(/^([A-Za-z]{1,2}\d{1,2}[A-Za-z]?)\s*(\d[A-Za-z]{2})$/);
                billingSplitPostcodeArray!!.shift()

                const deliverySplitPostcodeArray = useBillingAsDelivery ? null : (deliveryPostcode as string)
                    .replace(/\s/g, "")
                    .match(/^([A-Za-z]{1,2}\d{1,2}[A-Za-z]?)\s*(\d[A-Za-z]{2})$/);
                deliverySplitPostcodeArray?.shift()


                const billingAddress: CreateAddress = {
                    houseNumber: null,
                    houseName: null,
                    line1: billingLine1 as string,
                    line2: billingLine2NotBlank ? billingLine2 : null,
                    line3: null,
                    city: billingCity as string,
                    county: null,
                    postcode1: billingSplitPostcodeArray!![0],
                    postcode2: billingSplitPostcodeArray!![1]
                }

                const deliveryAddress = useBillingAsDelivery ? billingAddress : {
                    houseNumber: null,
                    houseName: null,
                    line1: deliveryLine1 as string,
                    line2: deliveryLine2NotBlank ? deliveryLine2 : null,
                    line3: null,
                    city: deliveryCity as string,
                    county: null,
                    postcode1: deliverySplitPostcodeArray!![0],
                    postcode2: deliverySplitPostcodeArray!![1]
                };

                const triggerSignup = ((paymentNonce: string | null,
                                              paymentDeviceData: string | null,
                                              paymentThreeDSecureAuthenticationId: string | null) => {
                    if (chosenPlanInfo) {
                        setLoading(true)
                        createOrder(applicationContext.reseller, applicationContext.salesPortalApiDomain, {
                            chosenPackageCode: chosenPlanInfo.packageCode,
                            packageBoltonCodes: chosenPlanInfo.planBolton ? [chosenPlanInfo.planBolton.code] : [],
                            customer: {
                                title: null,
                                firstName: forename,
                                lastName: surname,
                                dateOfBirth: dateOfBirth,
                                email: email,
                                externalCustomerRef: null
                            },
                            homeAddress: deliveryAddress,
                            directDebitDetails: null,
                            braintreePaymentNonce: paymentNonce,
                            braintreePaymentThreeDSecureAuthenticationId: paymentThreeDSecureAuthenticationId,
                            braintreePaymentDeviceData: paymentDeviceData,
                            marketingPreferences: {
                                emailMarketing: formData.get("emailMarketing") !== "on",
                                smsMarketing: formData.get("smsMarketing") !== "on",
                                postMarketing: formData.get("postMarketing") !== "on",
                                phoneMarketing: false,
                                automatedVoiceMarketing: false,
                                onlineMarketing: false,
                                inAppMarketing: false,
                                profiledMarketing: false,
                                thirdPartyMarketing: false
                            },
                            iccid: isSimInHandJourney ? readSessionItem("sim-details", isSimDetails)!!.iccid : null,
                            promoCode: promoCode ?? null,
                            loyaltyCard: null,
                            portInNumber: portInMsisdn?.value ?? null,
                            switchingCode: pacCode?.value ?? null,
                            referrerCode: maybeRafData?.referralCode ?? null,
                            referrerConfirmationId: maybeRafData?.referralCode ? "EI" : null,
                            referrerCustomerRef: maybeRafData?.referralCustomerRef ?? null
                        }).then((maybeOrderSummary) => {
                            if (isRequestError(maybeOrderSummary) || maybeOrderSummary.customerRef == null) {
                                navigate(SALES_PORTAL_ROUTES.InternalError(applicationContext.urlContext))
                            } else {
                                const orderConfirmation: OrderConfirmation = {
                                    packageCode: componentData.selectedPlan.packageCode,
                                    promoCode: componentData.selectedPlan.promoCode,
                                    email: email,
                                    houseNumber: null,
                                    building: null,
                                    line1: (useBillingAsDelivery ? billingLine1 : deliveryLine1) as string,
                                    line2: (useBillingAsDelivery && billingLine2NotBlank) ? billingLine2 : deliveryLine2NotBlank ? deliveryLine2 : null,
                                    city: (useBillingAsDelivery ? billingCity : deliveryCity) as string,
                                    postcode: (useBillingAsDelivery ? billingPostcode : deliveryPostcode) as string
                                }
                                saveLocalStorageItem("order-confirmation", orderConfirmation)
                                sessionStorage.removeItem(sessionStorageRafKey)
                                const dataLayer = {
                                    event: "purchase",
                                    items: [
                                        {
                                            item_id: componentData.selectedPlan.packageCode,
                                            item_name: simOnlyPlanDataLimit(componentData.selectedPlan) + " Data",
                                            item_brand: "THREE",
                                            item_variant: simOnlyPlanDataLimit(componentData.selectedPlan),
                                            price: simOnlyPlanMonthlyCost(componentData.selectedPlan).toString(),
                                            quantity: 1,
                                        }
                                    ],
                                    value: componentData.selectedPlanPriceWithPromo?.priceToPay?.toString() || simOnlyPlanMonthlyCost(componentData.selectedPlan).toString(),
                                    currency: "GBP",
                                    affiliation: isSimInHandJourney ? "SIH" : "WEB",
                                    coupon: promoCode,
                                    shipping: "0.00",
                                    tax: "20%"
                                }
                                TagManager.dataLayer({
                                    dataLayer: dataLayer
                                });
                                if (isSimInHandJourney) {
                                    TagManager.dataLayer({
                                        dataLayer: {
                                            event: "plan_activation"
                                        }
                                    })
                                    navigate(SALES_PORTAL_ROUTES.SimInHand.Confirmation(applicationContext.urlContext))
                                } else {
                                    navigate(SALES_PORTAL_ROUTES.SimOnly.Confirmation(applicationContext.urlContext))
                                }
                            }
                        })
                    }
                })

                if (braintreeInstance && paidInStorePlanPrice != undefined) {
                    braintreeInstance.requestPaymentMethod({
                        threeDSecure: {
                            amount: `${Math.max(deliveryPrice + chosenPlanInfo.monthlyPrice - paidInStorePlanPrice, 0)}`,
                            email: email,
                            billingAddress: {
                                givenName: truncateString(forename, 50),
                                surname: truncateString(surname, 50),
                                streetAddress: truncateString(billingLine1 as string, 50),
                                extendedAddress: truncateString(billingLine2NotBlank ? billingLine2 : "", 50),
                                locality: billingCity,
                                postalCode: billingPostcode
                            },
                            challengeRequested: true,
                        }
                    }, (error, payload) => {
                        if (error) {

                        } else {
                            triggerSignup(payload.nonce,
                                payload.deviceData || null,
                                (payload as cardPaymentMethodPayload)?.threeDSecureInfo?.threeDSecureAuthenticationId || ''
                            )
                        }
                    })
                } else {

                }
            } else {
                window.scrollTo({top: 0, behavior: "smooth"});
            }
        }

        const optDeliverySummaryCard = optHtmlContent(props.cmsContent, "sp-card-delivery-summary")
        const optPaidInStoreDisclaimer = optHtmlContent(props.cmsContent, "sp-card-paid-in-store-disclaimer")
        const optPaidInStoreDiscountSummary = optHtmlContent(props.cmsContent, "sp-card-paid-in-store-discount-summary")
        const optCreditComponent = optHtmlContent(props.cmsContent, "sp-credit-component")

        let formCmsContent = props.cmsContent.find((content) => content.reference === formId)

        if (chosenPlanInfo && formCmsContent && optDeliverySummaryCard && optPaidInStoreDisclaimer
            && optPaidInStoreDiscountSummary && optCreditComponent && paidInStorePlanPrice != undefined
            && isSimInHandJourney != undefined) {

            const priceToPay = deliveryPrice + (componentData.selectedPlanPriceWithPromo?.priceToPay ?? chosenPlanInfo.monthlyPrice) - paidInStorePlanPrice

            let plansComponent

            if (isSimInHandJourney) {
                plansComponent = simOnlyPlanCards
            } else {
                if (isDesktop) {
                    plansComponent = <>
                        <SimCardInfoSolo
                            cmsContent={props.cmsContent}
                            simOnlyPlan={chosenPlanInfo}
                            planHexColor={componentData.planHexColor}/>
                        <br/>
                    </>
                } else {
                    plansComponent = <SimCardInfoSolo
                        cmsContent={props.cmsContent}
                        simOnlyPlan={chosenPlanInfo}
                        planHexColor={componentData.planHexColor}/>
                }
            }

            const paymentSummary = (
                <PlanPurchasePaymentSummary
                    componentData={componentData}
                    setPromoCode={setPromoCode}
                    setSelectedPlanPriceWithPromo={() => {}}
                    setSelectedPlan={setSelectedPlan}
                    maybeRafData={maybeRafData}
                    chosenPlanInfo={chosenPlanInfo}
                    deliveryPrice={deliveryPrice}
                    paidInStorePlanPrice={paidInStorePlanPrice}
                    priceToPay={priceToPay}
                    isSimInHandJourney={isSimInHandJourney}
                    hasLeadGenerator={hasLeadGenerator}
                    cmsContent={props.cmsContent}
                    formId={formId}
                    promoCode={promoCode}
                />
            );

            return (
                <>
                    <style>
                        {`
                        /* css overrides to make the sim-container info card work for this page */
                        .sim-container {
                        min-height: 0 !important;
                        position: static !important;
                        margin-left: auto;
                        margin-right: auto;
                        }                                      
                        `}
                    </style>
                    <div id="loading-backdrop-wrapper">
                        <LoadingBackdrop isLoading={loading}/>
                        <HeaderFooterWrapper cmsContent={props.cmsContent} variant="primary">
                            {renderDynamicContent(optPurchasePage, [
                                {key: "TITLE", value: (isSimInHandJourney ? "Select a Plan" : "Your Plan")},
                                {
                                    key: "PLANS", value: plansComponent
                                },
                                {key: "MAYBE_PAYMENT_SUMMARY", value: (isDesktop ? paymentSummary: null)},
                                {
                                    key: "SIGN_UP_FORM",
                                    value: <SimOnlyPlanOnePagePurchaseForm formId={formId}
                                                                           cmsContent={props.cmsContent}
                                                                           submitForm={submitForm}
                                                                           setBraintreeInstance={setBraintreeInstance}
                                                                           braintreeInstance={braintreeInstance}
                                                                           paymentPurchaseSummary={ !isDesktop ? paymentSummary : null}
                                                                           isSimInHandJourney={isSimInHandJourney}
                                                                           amountToPay={priceToPay < 0 ? 0 : priceToPay}/>
                                }
                            ])}

                        </HeaderFooterWrapper>
                    </div>

                </>
            )
        } else {
            return null // Prevent page flicker while we are getting everything for the page.
        }

    } else {
        return null // Prevent page flicker while we are getting everything for the page.
    }
}

export default SimOnlyPlanOnePagePurchasePage
